import { useEffect, useMemo, useState } from 'react';
import type { Product } from '@rosita/shared';
import { api } from '../../lib/api';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { Button } from '../../components/ui/Button';
import { ProductCard } from '../../components/ui/ProductCard';
import { useAppSettings } from '../../lib/appSettings';
import { getProductBadges } from '../../lib/product';
import { DecoElement1, DecoElement2, DecoElement3 } from './DecoElements';
import styles from './HomePage.module.scss';

interface ProductListResponse {
  items: Product[];
  total: number;
}

const HERO_LEFT_IMG = '/home/hero/hero-1.webp';
const HERO_RIGHT_IMG = '/home/hero/hero-2.webp';
const DETAILS_IMAGES = [
  '/home/hero/hero-3.webp',
  '/home/hero/hero-4.webp',
  '/home/hero/hero-5.webp',
];

export default function HomePage(): JSX.Element {
  const [featured, setFeatured] = useState<Product[]>([]);
  const { newBadgeExpiryDays } = useAppSettings();

  useEffect(() => {
    void (async () => {
      try {
        const prodsRes = await api.get<ProductListResponse>(
          '/products?limit=12&sort=newest',
        );
        setFeatured(prodsRes.data.items);
      } catch {
        /* noop */
      }
    })();
  }, []);

  const collectionProducts = useMemo(() => {
    const newish = featured.filter(
      (p) => getProductBadges(p, newBadgeExpiryDays).isNew,
    );
    return (newish.length >= 4 ? newish : featured).slice(0, 4);
  }, [featured, newBadgeExpiryDays]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Rosita Store',
    url: typeof window !== 'undefined' ? window.location.origin : '',
  };

  return (
    <div className={styles.page}>
      <SEO title="Início" structuredData={structuredData} />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={`container-wide ${styles.heroInner}`}>
          <div className={styles.heroCopy}>
            <div className={styles.heroMain}>
              <h1 className={styles.heroTitle}>
                <span className={styles.heroWelcome}>{t.home.heroWelcome}</span>
                <span className={styles.heroBrand}>{t.home.heroBrand}</span>
              </h1>
              <p className={styles.heroTagline}>{t.home.heroTagline}</p>
              <p className={styles.heroBody}>{t.home.heroBody}</p>
            </div>
            <Button variant="primary" size="lg" to="/produtos">
              {t.home.heroCta}
            </Button>
          </div>

          <div className={styles.heroCollage} aria-hidden>
            <img
              src="/home/heart-group-1.svg"
              alt=""
              className={`${styles.heroHearts} ${styles.heroHeartsTop}`}
            />
            <figure className={`${styles.heroPolaroid} ${styles.heroPolaroidLeft}`}>
              <img src={HERO_LEFT_IMG} alt="" loading="eager" />
            </figure>
            <figure className={`${styles.heroPolaroid} ${styles.heroPolaroidRight}`}>
              <img src={HERO_RIGHT_IMG} alt="" loading="eager" />
            </figure>
            <img
              src="/home/heart-group-2.svg"
              alt=""
              className={`${styles.heroHearts} ${styles.heroHeartsBottom}`}
            />
          </div>
        </div>
      </section>

      {/* Collection */}
      <section className={styles.collection}>
        <header className={styles.collectionHeader}>
          <h2 className={`h1-display ${styles.collectionTitle}`}>
            {t.home.collectionTitle}{' '}
            <span className="accent">{t.home.collectionTitleAccent}</span>
          </h2>
          <p className={styles.collectionBody}>{t.home.collectionBody}</p>
        </header>
        {collectionProducts.length > 0 ? (
          <div className={`container-wide ${styles.collectionGrid}`}>
            {collectionProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        ) : null}
        <Button variant="primary" size="lg" to="/produtos">
          {t.home.collectionCta}
        </Button>
      </section>

      {/* Details — burgundy band */}
      <section className={styles.details}>
        <div className={`container-wide ${styles.detailsInner}`}>
          <div className={styles.detailsText}>
            <p className={styles.detailsEyebrow}>{t.home.detailsEyebrow}</p>
            <h2 className={styles.detailsTitle}>
              {t.home.detailsTitleLine1}
              <br />
              {t.home.detailsTitleLine2}
              <br />
              {t.home.detailsTitleLine3}
            </h2>
            <p className={styles.detailsBody}>{t.home.detailsBody}</p>
          </div>
          <div className={styles.detailsMedia}>
            {DETAILS_IMAGES.map((src) => (
              <div key={src} className={styles.detailsMediaItem}>
                <img src={src} alt="" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positives */}
      <section className={styles.positives}>
        <header className={styles.positivesHeader}>
          <h2 className={`h1-display ${styles.positivesTitle}`}>
            {t.home.positivesTitle}{' '}
            <span className="accent">{t.home.positivesTitleAccent}</span>
            {t.home.positivesTitleSuffix}
          </h2>
          <p className={styles.positivesBody}>{t.home.positivesBody}</p>
        </header>
        <div className={`container-wide ${styles.positivesGrid}`}>
          <article className={styles.positiveItem}>
            <div className={styles.positiveIcon}>
              <DecoElement1 />
            </div>
            <p className={styles.positiveCaption}>{t.home.positives.curatedTitle}</p>
          </article>
          <article className={styles.positiveItem}>
            <div className={styles.positiveIcon}>
              <DecoElement2 />
            </div>
            <p className={styles.positiveCaption}>{t.home.positives.humanTitle}</p>
          </article>
          <article className={styles.positiveItem}>
            <div className={styles.positiveIcon}>
              <DecoElement3 />
            </div>
            <p className={styles.positiveCaption}>{t.home.positives.shippingTitle}</p>
          </article>
        </div>
      </section>

      {/* Closing CTA */}
      <section className={styles.closing}>
        <img
          src="/home/rose-big-1.svg"
          alt=""
          aria-hidden
          className={`${styles.closingRose} ${styles.closingRoseLeft}`}
        />
        <img
          src="/home/rose-big-2.svg"
          alt=""
          aria-hidden
          className={`${styles.closingRose} ${styles.closingRoseRight}`}
        />
        <div className={styles.closingInner}>
          <h2 className={styles.closingTitle}>
            {t.home.closingTitle}{' '}
            <span className="accent">{t.home.closingTitleAccent}</span>
            {t.home.closingTitleSuffix}
          </h2>
          <p className={styles.closingBody}>{t.home.closingBody}</p>
          <Button variant="primary" size="lg" to="/produtos">
            {t.home.closingCta}
          </Button>
        </div>
      </section>
    </div>
  );
}
